/**
 * Transforms a GoodsItem GraphQL object to/from the detail form state.
 *
 * Like CuDeGoodsShipmentTransformer, toSchema() explicitly lists every
 * field — no spread of original to avoid leaking query-only fields.
 *
 * @see CDS spec: https://quip-amazon.com/haRbADR5yV7P/CuDe-Line-level-details
 */
import { BaseTransformer } from './BaseTransformer';
import { GoodsItemDetailFormState } from '../schemas/forms/goodsItemDetail';
import type {
  goodsItemFieldsFragment,
  EudrVerificationStatus,
} from '__generated__/graphql';

export type GoodsItemApiData = goodsItemFieldsFragment;

const blankEudrEntry =
  (): GoodsItemDetailFormState['eudrDdsEntries'][number] => ({
    isExempt: false,
    ddsReferenceNumber: null,
    ddsVerificationNumber: null,
    exemptionCode: null,
    status: null,
    verificationFailureReason: null,
    overrideReason: null,
    ddsSubmittedTimestamp: null,
    verifiedTimestamp: null,
    transactionId: null,
  });

interface EudrEntryIdentityFields {
  isExempt?: boolean | null;
  ddsReferenceNumber?: string | null;
  ddsVerificationNumber?: string | null;
  exemptionCode?: string | null;
}

const eudrEntryIdentity = (entry: EudrEntryIdentityFields): string =>
  entry.isExempt
    ? `exempt|${entry.exemptionCode?.trim() ?? ''}`
    : `dds|${entry.ddsReferenceNumber?.trim() ?? ''}|${
        entry.ddsVerificationNumber?.trim() ?? ''
      }`;

export class GoodsItemDetailTransformer extends BaseTransformer<
  GoodsItemApiData,
  GoodsItemDetailFormState
> {
  toForm(source: GoodsItemApiData): GoodsItemDetailFormState {
    const classifications = source.commodity?.classification ?? [];
    const adjustments = source.valuationAdjustment ?? [];
    const pkg = source.packaging?.[0];
    const imports = source.additionalReference ?? [];
    const govProc = source.governmentProcedure?.[0];
    const ddsEntries = source.eudrDueDiligenceStatements ?? [];

    return {
      // Header
      product: source.commodity?.product
        ? {
            id: source.commodity.product.id,
            version: source.commodity.product.version,
            clientIdentifier:
              source.commodity.product.clientIdentifier ?? undefined,
          }
        : null,
      productSku: source.externalReference?.sku ?? null,
      natureOfTransaction: source.transactionNature ?? null,
      quantity: source.goodsMeasure?.tariff ?? null,
      quantityUnit: source.goodsMeasure?.tariffUnit ?? null,
      totalValue: source.customsValue?.value ?? null,

      // Classification
      customsDescription: source.commodity?.description ?? null,
      countryOfOrigin: source.origin?.country ?? null,
      seller: source.seller
        ? { id: source.seller.id, name: source.seller.name ?? null }
        : null,
      // A goods item always has at least one HTS classification; seed a blank
      // row (type defaulted to HS) when none exist so HTS Number #1 shows on open.
      classifications: classifications.length
        ? classifications.map((c) => ({
            identification: c.identification ?? null,
            identificationType: c.identificationType ?? null,
            sequence: c.sequence ?? null,
          }))
        : [{ identification: null, identificationType: 'HS', sequence: null }],
      grossWeight: source.goodsMeasure?.grossWeight?.value ?? null,
      grossWeightUnit:
        source.goodsMeasure?.grossWeight?.value != null
          ? source.goodsMeasure?.grossWeight?.unit ?? 'KGM'
          : null,
      netWeight: source.goodsMeasure?.netNetWeight?.value ?? null,
      netWeightUnit:
        source.goodsMeasure?.netNetWeight?.value != null
          ? source.goodsMeasure?.netNetWeight?.unit ?? 'KGM'
          : null,

      // Customs Valuation
      valuationMethod: source.customsValuation?.method ?? null,
      valuationAdjustments: adjustments.map((a) => ({
        type: a.addition ?? null,
        amount: a.amount?.value ?? null,
        currency: a.amount?.currency ?? null,
        percentage: a.percentage ?? null,
      })),

      // Packaging
      packagingType: pkg?.type ?? null,
      packagingQuantity: pkg?.quantity ?? null,
      shippingMarks: pkg?.marksNumbers ?? null,

      // Regulations
      importControls: imports.map((r) => ({
        code: r.type ?? null,
        reference: r.identification ?? null,
      })),
      governmentProcedure: govProc?.current ?? null,
      previousProcedure: govProc?.previous ?? null,
      additionalCode: govProc?.additionalProcedure?.[0]?.procedure ?? null,

      eudrDdsEntries: ddsEntries.length
        ? ddsEntries.map((e) => ({
            isExempt: e.isExempt ?? false,
            ddsReferenceNumber: e.ddsReferenceNumber ?? null,
            ddsVerificationNumber: e.ddsVerificationNumber ?? null,
            exemptionCode: e.exemptionCode ?? null,
            status: e.status ?? null,
            verificationFailureReason: e.verificationFailureReason ?? null,
            overrideReason: e.overrideReason ?? null,
            ddsSubmittedTimestamp: e.ddsSubmittedTimestamp ?? null,
            verifiedTimestamp: e.verifiedTimestamp ?? null,
            transactionId: e.transactionId ?? null,
          }))
        : [blankEudrEntry()],
    };
  }

  toSchema(
    formData: GoodsItemDetailFormState,
    original: GoodsItemApiData,
  ): GoodsItemApiData {
    const savedIdentities = new Set(
      (original.eudrDueDiligenceStatements ?? []).map(eudrEntryIdentity),
    );
    return {
      sequence: original.sequence,
      transactionNature: formData.natureOfTransaction,
      customsValue:
        formData.totalValue != null
          ? {
              value: formData.totalValue,
              currency: original.customsValue?.currency,
            }
          : original.customsValue,
      goodsMeasure: {
        grossWeight:
          formData.grossWeight != null
            ? { value: formData.grossWeight, unit: formData.grossWeightUnit }
            : null,
        netNetWeight:
          formData.netWeight != null
            ? { value: formData.netWeight, unit: formData.netWeightUnit }
            : null,
        tariff: formData.quantity,
        tariffUnit: formData.quantityUnit,
      },
      commodity: {
        description: formData.customsDescription,
        // Drop rows without an HTS code so an unfilled seeded row is not persisted.
        classification: formData.classifications
          .filter((c) => c.identification)
          .map((c) => ({
            identification: c.identification,
            identificationType: c.identificationType,
            sequence: c.sequence,
          })),
        dutyTaxFees: original.commodity?.dutyTaxFees,
        // Saved as IdVersionInput (id+version); cast to the query-shaped
        // product type, whose clientIdentifier is read-only/query-only.
        product: formData.product
          ? ({
              id: formData.product.id,
              version: formData.product.version,
            } as NonNullable<GoodsItemApiData['commodity']>['product'])
          : undefined,
      },
      origin: {
        country: formData.countryOfOrigin,
        type: original.origin?.type,
      },
      customsValuation: {
        method: formData.valuationMethod,
        freightCharge: original.customsValuation?.freightCharge,
        insurance: original.customsValuation?.insurance,
        chargeDeduction: original.customsValuation?.chargeDeduction,
      },
      valuationAdjustment: formData.valuationAdjustments
        .filter((a) => a.type)
        .map((a) => ({
          addition: a.type,
          amount:
            a.amount != null ? { value: a.amount, currency: a.currency } : null,
          percentage: a.percentage,
        })),
      packaging: [
        {
          type: formData.packagingType,
          quantity: formData.packagingQuantity,
          marksNumbers: formData.shippingMarks,
          ...(original.packaging?.[0]?.sequence != null
            ? { sequence: original.packaging[0].sequence }
            : {}),
        },
      ],
      additionalReference: formData.importControls.map((c) => ({
        type: c.code,
        identification: c.reference,
      })),
      eudrDueDiligenceStatements: formData.eudrDdsEntries
        .filter((e) =>
          e.isExempt
            ? e.exemptionCode?.trim()
            : e.ddsReferenceNumber?.trim() || e.ddsVerificationNumber?.trim(),
        )
        .map((e) => {
          const isSaved = savedIdentities.has(eudrEntryIdentity(e));
          return {
            isExempt: e.isExempt,
            ddsReferenceNumber: e.isExempt ? null : e.ddsReferenceNumber,
            ddsVerificationNumber: e.isExempt ? null : e.ddsVerificationNumber,
            exemptionCode: e.isExempt ? e.exemptionCode : null,
            status: isSaved
              ? (e.status as EudrVerificationStatus | null)
              : null,
            verificationFailureReason: isSaved
              ? e.verificationFailureReason
              : null,
            overrideReason: isSaved ? e.overrideReason : null,
            ddsSubmittedTimestamp: isSaved ? e.ddsSubmittedTimestamp : null,
            verifiedTimestamp: isSaved ? e.verifiedTimestamp : null,
            transactionId: isSaved ? e.transactionId : null,
          };
        }),
      governmentProcedure: [
        {
          current: formData.governmentProcedure,
          previous: formData.previousProcedure,
          description: original.governmentProcedure?.[0]?.description,
          additionalProcedure: formData.additionalCode
            ? [
                {
                  procedure: formData.additionalCode,
                  sequence:
                    original.governmentProcedure?.[0]?.additionalProcedure?.[0]
                      ?.sequence ?? 1,
                },
              ]
            : original.governmentProcedure?.[0]?.additionalProcedure ?? [],
        },
      ],
      seller: formData.seller
        ? ({ id: formData.seller.id } as GoodsItemApiData['seller'])
        : undefined,
      // Manually-entered product number. Persisted only when no product record
      // is selected, matching the schema's "sku only if no product" contract.
      externalReference:
        !formData.product && formData.productSku
          ? { sku: formData.productSku }
          : undefined,
    };
  }
}
